import { Pill, VisuallyHidden } from '@mantine/core';
import Image from 'next/image';
import type { ReactNode } from 'react';

import {
	CommunicationLinesDescription,
	MeterCabinetDescription,
	MeterDescription,
	PointOfAttachmentDescription,
	PoleGroundDescription,
	PowerDistributionPrimaryLinesDescription,
	PowerNeutralLineDescription,
	ServiceDropDescription,
	ServiceEntranceConductorDescription,
	ServiceMastDescription,
	ServiceMastGuyDescription,
	ServicePanelCircuitBreakerDescription,
	TreeTrimServiceLineDescription,
	UtilityPoleDescription,
	UtilityTransformerDescription,
	WeatherheadDescription,
} from '../../app/(client)/(authenticated)/reportProblem/questions/(premise)/problemType/EquipmentDescriptions';
import {
	HelpContent,
	HelpContentHeading,
} from '../../app/(client)/(authenticated)/reportProblem/questions/(premise)/problemType/HelpContent';
import { EquipmentOwner } from './EquipmentOwner';
import equipmentResponsibility from './equipmentResponsibility.png';
import styles from './EquipmentResponsibilityDetails.module.scss';
import type { OwnershipRow } from './OwnershipRow';
import { ResponsibilityTable } from './ResponsibilityTable';

const ownershipData: readonly OwnershipRow[] = [
	{ name: 'Meter', owner: EquipmentOwner.DTE, description: <MeterDescription /> },
	{ name: 'Pole ground', owner: EquipmentOwner.DTE, description: <PoleGroundDescription /> },
	{
		name: 'Primary lines',
		owner: EquipmentOwner.DTE,
		description: <PowerDistributionPrimaryLinesDescription />,
	},
	{
		name: 'Power neutral line',
		owner: EquipmentOwner.DTE,
		description: <PowerNeutralLineDescription />,
	},
	{ name: 'Service line', owner: EquipmentOwner.DTE, description: <ServiceDropDescription /> },
	{
		name: 'Transformer',
		owner: EquipmentOwner.DTE,
		description: <UtilityTransformerDescription />,
	},
	{
		name: 'Cable lines',
		owner: EquipmentOwner.OtherUtility,
		description: <CommunicationLinesDescription />,
	},
	{
		name: 'Communication lines',
		owner: EquipmentOwner.OtherUtility,
		description: <CommunicationLinesDescription />,
	},
	{
		name: 'Meter socket & jaws',
		owner: EquipmentOwner.Customer,
		description: <MeterCabinetDescription />,
	},
	{
		name: 'Point of attachment',
		owner: EquipmentOwner.Customer,
		description: <PointOfAttachmentDescription />,
	},
	{
		name: 'Service entrance conductor',
		owner: EquipmentOwner.Customer,
		description: <ServiceEntranceConductorDescription />,
	},
	{ name: 'Service mast', owner: EquipmentOwner.Customer, description: <ServiceMastDescription /> },
	{
		name: 'Service mast guy',
		owner: EquipmentOwner.Customer,
		description: <ServiceMastGuyDescription />,
	},
	{
		name: 'Service panel',
		owner: EquipmentOwner.Customer,
		description: <ServicePanelCircuitBreakerDescription />,
	},
	{
		name: 'Tree trim (service line)',
		owner: EquipmentOwner.Customer,
		description: <TreeTrimServiceLineDescription />,
	},
	{ name: 'Utility pole', owner: EquipmentOwner.DTE, description: <UtilityPoleDescription /> },
	{ name: 'Weatherhead', owner: EquipmentOwner.Customer, description: <WeatherheadDescription /> },
] as const satisfies readonly OwnershipRow[];

export function EquipmentResponsibilityDetails(): ReactNode {
	return (
		<HelpContent>
			<HelpContentHeading>
				<h4>Equipment responsibility</h4>
				<Pill.Group>
					<Pill>Utility owned and customer owned equipment</Pill>
				</Pill.Group>
			</HelpContentHeading>

			<figure className={styles['responsibilityFigure']}>
				<Image
					src={equipmentResponsibility}
					alt="Diagram of equipment with labels of what is DTE's responsibility and what is the customer's responsibility"
					className={styles['responsibilityImage']}
				/>
				<VisuallyHidden component="figcaption">
					Diagram of equipment with labels indicating which equipment is DTE's responsibility and which
					equipment is the customer's responsibility.
				</VisuallyHidden>
			</figure>

			<ResponsibilityTable data={ownershipData} />
		</HelpContent>
	);
}
